import React, { useState } from 'react';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import useMeasure from 'react-use-measure';
import { ResizeObserver } from '@juggle/resize-observer';
import { Link, navigate } from 'gatsby';
import { useMediaQuery } from 'react-responsive';

import H from '../styled/Heading';
import B from '../styled/Button';

import ArrowSvg from '../../assets/arrow-right-2.svg';
import XSvg from '../../assets/x.svg';

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  border-bottom: 2px solid ${({ theme }) => theme.colorMain};

  @media only screen and (max-width: 810px) {
    border-width: 1px;
  }

  &:last-of-type {
    border-bottom: 0;
  }
`;

const Title = styled.button`
  position: relative;
  width: 100%;
  text-align: left;
  padding: 8px 100px 10px 0;
  transition: padding 0.15s ease-in-out;

  @media only screen and (max-width: 810px) {
    padding: 4px 30px 4px 0;
  }
`;

const TitleText = styled(H)`
  line-height: 1;
  padding-top: 5px;
  font-size: 29px;
  line-height: 35px;

  @media only screen and (max-width: 810px) {
    line-height: 1;
  }
`;

const OpenClose = styled.div`
  position: absolute;
  right: 0;
  top: 21px;
  transition: transform 0.15s ease-in-out;
  transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0deg)')};

  @media only screen and (max-width: 810px) {
    top: 14px;
  }
`;

const X = styled(XSvg)`
  height: 16px;
  width: 16px;

  @media only screen and (max-width: 810px) {
    height: 18px;
    width: 18px;
  }

  path,
  rect {
    fill: ${({ theme }) => theme.colorMain} !important;
  }
`;

const Categories = styled.div`
  margin-bottom: 14px;
  padding-bottom: 7px;
  padding-right: 120px;

  @media only screen and (max-width: 810px) {
    padding-right: 0px;
    margin-bottom: 16px;
  }
`;

const Button = styled(B)`
  margin-right: 14px;
  margin-bottom: 7px;
  font-size: 20px;
  line-height: 24px;
  padding-bottom: 1px;

  @media only screen and (max-width: 810px) {
    font-size: 16px;
    line-height: 19px;
    margin-right: 7px;
  }
`;

const Text = styled(({ showBg, ...rest }) => <animated.div {...rest} />)`
  overflow: hidden;
`;

const LinkWrapper = styled.span`
  @media only screen and (max-width: 810px) {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;

const LinkToProject = styled(H)`
  position: absolute;
  right: 0;
  bottom: 5px;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: ${({ theme }) => theme.fontSans};
  font-size: 20px;
  line-height: 24px;

  @media only screen and (max-width: 810px) {
    display: inline-flex;
    position: relative;
    padding: 4px 7px;
    bottom: 12px;
  }
`;

const Arrow = styled(ArrowSvg)`
  width: 69px;
  height: 24px;

  @media only screen and (max-width: 810px) {
    width: 69px;
    height: 24px;
  }

  path,
  rect {
    stroke: ${({ theme }) => theme.colorMain} !important;
  }
`;

const SmallAccordion = ({
  title,
  text,
  first = false,
  open,
  update,
  index,
  data,
  isEn,
}) => {
  const [textRef, { height }] = useMeasure({ polyfill: ResizeObserver });

  const isMob = useMediaQuery({
    query: '(max-width: 810px)',
  });

  const springProps = useSpring({
    height: open ? height : 0,
    // config: { duration: 300 },
  });

  const navigateToProject = url => {
    navigate(url);
  };

  return (
    <Wrapper
      first={first}
      onClick={() =>
        open && !isMob && 
        navigateToProject(
          isEn ? `/en/works/${data.slug}` : `/works/${data.slug}`
        )
      }
    >
      <Title
        onClick={() => {
          isMob && update(index, !open);
        }}
        open={open}
        onMouseEnter={() => {
          !isMob && update(index, true)
        }}
      >
        <TitleText size={1}>{data.title}</TitleText>
        <OpenClose
          open={open}
          onClick={() => {
            open && update(index, !open);
          }}
        >
          <X />
        </OpenClose>
      </Title>
      {data.projectCategories && (
        <Text style={{ ...springProps }}>
          <div ref={textRef} style={{ position: 'relative' }}>
            <Categories>
              {data.projectCategories.map(item => {
                return (
                  <Button
                    to={
                      isEn
                        ? `/en/works?cat=${item.slug}`
                        : `/works?cat=${item.slug}`
                    }
                    small
                    key={item.title}
                  >
                    {item.title}
                  </Button>
                );
              })}
            </Categories>
            <LinkWrapper>
              <LinkToProject
                size={6}
                as={Link}
                to={isEn ? `/en/works/${data.slug}` : `/works/${data.slug}`}
              >
                {isEn ? 'more' : 'zobacz'}
                <Arrow />
              </LinkToProject>
            </LinkWrapper>
          </div>
        </Text>
      )}
    </Wrapper>
  );
};

export default SmallAccordion;
